<!-- ORGANISM: LoginFooter.vue -->

<script setup lang="ts">
//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Vuetify
import { useDisplay } from "vuetify";

// Components
import FooterBrand from "../molecules/FooterBrand.vue";
import FooterLinks from "../molecules/FooterLinks.vue";
import FooterCopyright from "../molecules/FooterCopyright.vue";

//
// ============================================================================
// TYPES
// ============================================================================
//

interface DescriptionLink {
  keyword: string;
  url: string;
  tooltip?: string;
}

//
// ============================================================================
// PROPS
// ============================================================================
//

interface Props {
  logoSrc?: string | null;
  logoAlt?: string;
  logoSize?: number;
  title?: string | null;
  subtitle?: string;
  description?: string;
  disclaimer?: string;
  descriptionLinks?: DescriptionLink[];
}

const props = withDefaults(defineProps<Props>(), {
  logoSrc: null,
  logoAlt: "Laboratory Logo",
  logoSize: 36,
  title: null,
  subtitle: "Laboratory Information System",
  description:
    "This is a description about the laboratory. It may contain important information and links.",
  disclaimer: "This is a disclaimer about the laboratory and its services.",
  descriptionLinks: () => [],
});

//
// ============================================================================
// COMPOSABLES
// ============================================================================
//

// Localization
const { t } = useI18n();

// Breakpoints
const { smAndDown } = useDisplay();

// Runtime Config
const config = useRuntimeConfig();

//
// ============================================================================
// COMPUTED PROPERTIES
// ============================================================================
//

const resourceLinks = computed(() => [
  {
    text: t("pages.login.loginFooter.labTests"),
    href: pathCombine(config.public.APP_BASE_URL, "/tests"),
    icon: "mdi-flask-outline",
    external: false,
    tooltip: t("pages.login.loginFooter.labTestsTooltip"),
  },
  {
    text: t("pages.login.loginFooter.pricing"),
    href: pathCombine(config.public.APP_BASE_URL, "/pricing"),
    icon: "mdi-currency-usd",
    external: false,
    tooltip: t("pages.login.loginFooter.pricingTooltip"),
  },
  {
    text: t("pages.login.loginFooter.covidTesting"),
    href: pathCombine(config.public.APP_BASE_URL, "/covid"),
    icon: "mdi-virus-outline",
    external: false,
    tooltip: t("pages.login.loginFooter.covidTestingTooltip"),
  },
  {
    text: t("pages.login.loginFooter.documentation"),
    href: pathCombine(config.public.APP_BASE_URL, "/docs"),
    icon: "mdi-book-open-outline",
    external: false,
    tooltip: t("pages.login.loginFooter.documentationTooltip"),
  },
]);

const connectLinks = computed(() => [
  {
    text: t("pages.login.loginFooter.locationDetails"),
    href: "https://maps.google.com/?q=София,бул.Витоша+1",
    icon: "mdi-map-marker-outline",
    external: true,
    tooltip: t("pages.login.loginFooter.locationDetailsTooltip"),
  },
  {
    text: `${t("pages.login.loginFooter.phoneNumber")}`,
    href: `tel:${t("pages.login.loginFooter.phoneNumber").replace(/\s/g, "")}`,
    icon: "mdi-phone-outline",
    external: false,
    tooltip: t("pages.login.loginFooter.phoneTooltip"),
  },
  {
    text: t("pages.login.loginFooter.emailAddress"),
    href: `mailto:${t("pages.login.loginFooter.emailAddress")}`,
    icon: "mdi-email-outline",
    external: false,
    tooltip: t("pages.login.loginFooter.emailTooltip"),
  },
  {
    text: t("pages.login.loginFooter.workingHoursDetails"),
    href: "#",
    icon: "mdi-clock-outline",
    external: false,
    tooltip: t("pages.login.loginFooter.workingHoursTooltip"),
  },
]);
</script>

<template>
  <v-footer class="border-t py-sm-4 bg-white">
    <v-container class="px-0">
      <v-row class="d-flex justify-space-between">
        <!-- Left: Laboratory Brand -->
        <v-col cols="12" lg="6" :class="smAndDown ? 'pr-0' : 'pr-16'">
          <footer-brand
            :logo-src="logoSrc"
            :logo-alt="logoAlt"
            :logo-size="logoSize"
            :title="title"
            :subtitle="subtitle"
            :description="description"
            :disclaimer="disclaimer"
            :description-links="descriptionLinks"
          />
        </v-col>

        <!-- Middle: Services Links -->
        <v-col cols="12" sm="6" lg="3">
          <footer-links :title="t('pages.login.loginFooter.resources')" :links="resourceLinks" />
        </v-col>

        <!-- Right: Contact Links -->
        <v-col cols="12" sm="6" lg="3">
          <footer-links :title="t('pages.login.loginFooter.connect')" :links="connectLinks" />
        </v-col>
      </v-row>

      <!-- Separator -->
      <v-divider class="my-8" />

      <!-- Bottom Section: Copyright -->
      <v-row align="center" justify="center">
        <footer-copyright
          :copyright-prefix="t('pages.login.loginFooter.copyrightPrefix')"
          :company-name="t('pages.login.loginFooter.skywareGroup')"
          :company-url="t('pages.login.loginFooter.skywareGroupUrl')"
          :company-tooltip="t('pages.login.loginFooter.skywareGroupTooltip')"
          :product-name="t('pages.login.loginFooter.lisILab')"
          :product-url="t('pages.login.loginFooter.skywareGroupUrl')"
          :product-tooltip="t('pages.login.loginFooter.limsILabTooltip')"
          :version-label="t('pages.login.loginFooter.versionLabel')"
        />
      </v-row>
    </v-container>
  </v-footer>
</template>
